import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import Button from '@mui/material/Button';
import React, { FC, useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import useSWR from 'swr';
import { colors, fontSize, fontWeight, TypographyStyledGlobal } from '../../../../theme';
import useFetcher from '../../../../utils/hooks/useFetcher';
import Loader from '../../../Loader/loader';
import QuestionListPanel from '../../../QuestionListPanel/QuestionListPanel';
import SelectedQuestionsPanel from '../../../SelectedQuestionsPanel/SelectedQuestionsPanel';
import SimpleActionButton from '../../ActionButton/simpleActionButton';
import { createDefaultSettings, SelectedQuestion, SelectedQuestionSettings, SelectedSurveyProps, SurveyQuestion } from '../addSurveyInterface';
import { LoaderContentStyled } from '../styled';
import {
    StepConfigureActionsStyled,
    StepConfigureContainerStyled,
    StepConfigureFooterStyled,
    StepConfigurePanelStyled,
} from './styledStepConfigure';

interface StepSelectQuestionProps {
    selectedSurvey: SelectedSurveyProps;
    selectedQuestions: SelectedQuestion[];
    onSelectedQuestionsChange: (questions: SelectedQuestion[]) => void;
    handleNextStep: (formId: string) => void
}

const StepSelectQuestion: FC<StepSelectQuestionProps> = ({
    selectedSurvey,
    selectedQuestions,
    onSelectedQuestionsChange,
    handleNextStep,
}) => {
    const { t } = useTranslation('settings');
    const fetcher = useFetcher();
    const { data: questions, error } = useSWR<SurveyQuestion[]>(
        `${process.env.REACT_APP_API_URL}/askia/surveys/${selectedSurvey.id}/questions`,
        fetcher,
    );

    const [pendingSelectedIds, setPendingSelectedIds] = useState<number[]>([]);

    const selectedQuestionIds = selectedQuestions.map((sq) => sq.question.id);
    const availableQuestions = (questions ?? []).filter((q) => !selectedQuestionIds.includes(q.id));
    const handleAddSelected = () => {
        if (!questions) { return; }
        const questionsToAdd = questions.filter((q) => pendingSelectedIds.includes(q.id));
        onSelectedQuestionsChange([
            ...selectedQuestions,
            ...questionsToAdd.map((q) => ({ customName: q.details.analysecaption || q.details.maincaption, question: q, settings: createDefaultSettings(q) })),
        ]);
        setPendingSelectedIds([]);
    };

    const handleRename = useCallback(
        (questionId: number, name: string) => {
            onSelectedQuestionsChange(
                selectedQuestions.map((sq) => (sq.question.id === questionId ? { ...sq, customName: name } : sq)),
            );
        },
        [selectedQuestions, onSelectedQuestionsChange],
    );

    const handleRemove = useCallback(
        (questionId: number) => {
            onSelectedQuestionsChange(selectedQuestions.filter((sq) => sq.question.id !== questionId));
        },
        [selectedQuestions, onSelectedQuestionsChange],
    );

    const handleConfigure = useCallback(
        (questionId: number, customName: string, settings: SelectedQuestionSettings) => {
            onSelectedQuestionsChange(
                selectedQuestions.map((sq) => {
                    if (sq.question.id === questionId) { return { ...sq, customName, settings }; }

                    return sq;
                }),
            );
        },
        [selectedQuestions, onSelectedQuestionsChange],
    );

    if (error) {
        return (
            <LoaderContentStyled>
                <ErrorOutlineIcon sx={{ color: colors.alertDanger, flexShrink: 0, fontSize: 18 }} />
                <div>
                    <TypographyStyledGlobal color={colors.alertDanger} size={fontSize.sm} weight={fontWeight.regular}>
                        {t('tabs.createSurvey.step3Content.errorLoadingQuestions')}
                    </TypographyStyledGlobal>
                    <TypographyStyledGlobal color={colors.grey300} size={fontSize.s} weight={fontWeight.light}>
                        {error.message}
                    </TypographyStyledGlobal>
                </div>
            </LoaderContentStyled>
        );
    }

    if (!questions) {
        return (
            <LoaderContentStyled>
                <Loader size={14} />
                <TypographyStyledGlobal color={colors.grey200} size={fontSize.sm} weight={fontWeight.light}>
                    {t('tabs.createSurvey.step3Content.loadingQuestions')}
                </TypographyStyledGlobal>
            </LoaderContentStyled>
        );
    }

    return (
        <>
            <StepConfigureContainerStyled>
                <StepConfigurePanelStyled>
                    <QuestionListPanel
                        onSelectionChange={setPendingSelectedIds}
                        questions={availableQuestions}
                        selectedIds={pendingSelectedIds}
                        title={t('tabs.createSurvey.step3Content.availableQuestionsTitle')}
                    />
                </StepConfigurePanelStyled>

                <StepConfigureActionsStyled>
                    <Button
                        disabled={pendingSelectedIds.length === 0}
                        onClick={handleAddSelected}
                        sx={{
                            backgroundColor: colors.default,
                            color: colors.grey000,
                            minWidth: 0,
                            padding: '8px',
                            borderRadius: '8px',
                            '&:hover': { backgroundColor: colors.defaultHover },
                            '&.Mui-disabled': { backgroundColor: colors.grey500, color: colors.grey400 },
                        }}
                        variant="contained"
                    >
                        <ArrowForwardIcon sx={{ fontSize: 18 }} />
                    </Button>
                    <TypographyStyledGlobal
                        color={colors.grey400}
                        size={fontSize.s}
                        style={{ textAlign: 'center', maxWidth: '60px' }}
                    >
                        {t('tabs.createSurvey.step3Content.addSelectedHint', { count: pendingSelectedIds.length })}
                    </TypographyStyledGlobal>
                </StepConfigureActionsStyled>

                <StepConfigurePanelStyled>
                    <SelectedQuestionsPanel
                        onConfigure={handleConfigure}
                        onRemove={handleRemove}
                        onRename={handleRename}
                        onReorder={onSelectedQuestionsChange}
                        selectedQuestions={selectedQuestions}
                        title={t('tabs.createSurvey.step3Content.selectedQuestionsTitle')}
                    />
                </StepConfigurePanelStyled>
            </StepConfigureContainerStyled>
            <StepConfigureFooterStyled>
                <SimpleActionButton handleClickConfirm={() => handleNextStep('#step3')} labelConfirm={t('form.nextStep')} />
            </StepConfigureFooterStyled>
        </>
    );
};

export default StepSelectQuestion;
